// Simplified AuthService for frontend2
// Based on frontend/src/services/auth/auth.service.tsx
import { ContextHolder } from '@frontegg/rest-api';

export interface PlbAuthState {
  sub: string;
  name: string;
  email: string;
  tenant_id: string;
  roles: Array<string>;
  tenant_name: string;
  access_token: string;
  profile_img_url?: string;
  tenants?: Array<{
    tenantId: string;
    name: string;
  }>;
  customClaims?: Record<string, any>;
}

declare global {
  interface Window {
    analytics: any;
    thena: any;
    __PLB_ENV__?: Record<string, any>;
  }
}

export default class AuthService {
  private static instance: AuthService;

  authUrl: string;
  authAppUrl: string;
  apiUrl: string | undefined;
  useExternalLogin: boolean;
  feClientId: string | undefined;
  feAppId: string | undefined;
  plbAuthState: PlbAuthState | null = null;
  isCustomDomain: boolean = false;
  oidcEnabled: boolean = false;
  oidcLoginUrl: string | null = null;
  oidcLogoutUrl: string | null = null;
  legacyAppUrl: string | undefined;
  handoffToken: string | null = null;
  noDataAccess: boolean = false;
  noDataAccessLogoUrl: string | null = null;
  // Set when bootstrap (env/init or __PLB_ENV__) could not produce a usable
  // config. App.tsx checks this and renders a clear error instead of mounting
  // FronteggProvider with empty config (which would surface a confusing
  // "Couldn't sign you in" screen).
  bootstrapError: string | null = null;
  private _checkedPartnerAccess: boolean = false;

  // Minimal listener pattern so components can subscribe to auth-token
  // changes — used by useAuthToken (src/hooks/useAuthToken.ts) to gate
  // queries that must wait for auth to arrive. Only `notifyAuthChanged()`
  // fires listeners; callers must opt-in, so mutating plbAuthState does
  // not cascade re-renders on its own (that caused render loops before).
  private listeners = new Set<() => void>();

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  }

  notifyAuthChanged(): void {
    this.listeners.forEach((fn) => {
      try { fn(); } catch { /* isolate listener failures */ }
    });
  }

  getAuthTokenSnapshot(): string | null {
    return this.handoffToken || this.plbAuthState?.access_token || null;
  }


  constructor() {
    const host = window.location.host;
    this.useExternalLogin = false;

    // Read env config — injected into HTML by Flask, or fetched via XHR in local dev
    let config = window.__PLB_ENV__;
    // Never reach for the network under test. The fetch below is a *synchronous*
    // XHR, and the spec forbids a timeout on one, so a backend that accepts the
    // connection without answering blocks the calling thread forever. Any test
    // whose import graph reaches this constructor would hang rather than fail —
    // which is exactly what happened: a wedged local server on :5501 took the
    // whole frontend suite down with no output and no failure.
    //
    // `import.meta.env.MODE` is statically replaced at build time, so this whole
    // branch compiles out of the production bundle.
    const underTest = import.meta.env?.MODE === 'test';
    if (!config && underTest) {
      config = undefined;
    } else if (!config) {
      try {
        const isLocal = host.startsWith('local.') || host.startsWith('localhost')
          || host.endsWith('.local.pliable.boston') || host.includes('.local.pliable.boston:');
        const envInitUrl = isLocal
          ? 'https://local.whetstonedata.com:5501/api/v1/env/init'
          : '/api/v1/env/init';
        const xhr = new XMLHttpRequest();
        xhr.open('GET', envInitUrl, false);
        xhr.send();
        if (xhr.status === 200) {
          config = JSON.parse(xhr.responseText);
        } else {
          this.bootstrapError =
            `Couldn't reach the Pliable backend at ${envInitUrl} (status ${xhr.status}).`;
        }
      } catch (e) {
        console.warn('[AuthService] env/init fallback failed:', e);
        this.bootstrapError =
          `Couldn't reach the Pliable backend. Make sure the API is running.`;
      }
    }

    if (config) {
      this.authUrl = config.fe_auth_url;
      this.authAppUrl = config.app_url;
      this.apiUrl = config.api_url;
      this.feClientId = config.fe_client_id;
      this.feAppId = config.fe_app_id;
      this.legacyAppUrl = config.legacy_app_url;
      this.isCustomDomain = config.is_custom_domain || false;
      this.oidcEnabled = config.oidc_enabled || false;
      this.oidcLoginUrl = config.oidc_login_url || null;
      this.oidcLogoutUrl = config.oidc_logout_url || null;

      console.log(`[AuthService] env=${config.app_env}, domain=${host}, custom=${this.isCustomDomain}, oidc=${this.oidcEnabled}`);

      // Embed route owns its own auth — skip OIDC auto-exchange + session
      // recovery so the page can handle code-for-JWT exchange explicitly.
      // Without this, an empty embed URL (no oidc_code) would fall through
      // to the OIDC redirect-to-login branch below.
      if (window.location.pathname === '/embed') {
        return;
      }

      // Embed sessions recover via a cookie-backed server session (mirrors
      // the OIDC /oidc/session pattern). The /auth-exchange response set a
      // Partitioned HttpOnly cookie; GET /env/embed-session swaps that
      // cookie for the JWT. This survives full-page nav, Safari tab
      // discard, and — critically — works inside cross-origin iframes
      // (sessionStorage is often partitioned away by ITP in that context).
      try {
        const sessionXhr = new XMLHttpRequest();
        sessionXhr.open('GET', `/api/v1/env/embed-session`, false);
        sessionXhr.withCredentials = true;
        sessionXhr.send();
        if (sessionXhr.status === 200) {
          const data = JSON.parse(sessionXhr.responseText);
          const embedToken: string | undefined = data?.access_token;
          if (embedToken) {
            const payload = JSON.parse(
              atob(embedToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
            );
            const notExpired = payload.exp && payload.exp * 1000 > Date.now();
            if (notExpired && payload.type === 'embedToken') {
              ContextHolder.setAccessToken(embedToken);
              this.handoffToken = embedToken;
              this.setAuthState({
                sub: payload.sub || '',
                name: payload.email || payload.sub || '',
                email: payload.email || '',
                tenant_id: payload.tenantId || '',
                roles: (payload.roles || []).map((r: any) =>
                  typeof r === 'string' ? r : r.key || '',
                ),
                tenant_name: payload.tenantId || '',
                access_token: embedToken,
                tenants: [],
                customClaims: {},
              });
              return;
            }
          }
        }
      } catch {
        // Embed recovery is optional — fall through to OIDC flow below.
      }

      // --- OIDC flow (direct IdP integration) ---
      if (this.oidcEnabled) {
        const oidcParams = new URLSearchParams(window.location.search);
        const oidcCode = oidcParams.get('oidc_code');

        if (oidcCode) {
          // Returning from OIDC callback — exchange code for JWT
          try {
            const exchangeXhr = new XMLHttpRequest();
            exchangeXhr.open('GET', `/api/v1/env/auth-exchange?code=${encodeURIComponent(oidcCode)}`, false);
            exchangeXhr.send();
            if (exchangeXhr.status === 200) {
              const data = JSON.parse(exchangeXhr.responseText);
              if (data.access_token) {
                ContextHolder.setAccessToken(data.access_token);
                this.handoffToken = data.access_token;

                // Decode JWT and populate auth state
                try {
                  const payload = JSON.parse(atob(data.access_token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
                  this.setAuthState({
                    sub: payload.sub || '',
                    name: payload.name || '',
                    email: payload.email || '',
                    tenant_id: payload.tenantId || '',
                    roles: (payload.roles || []).map((r: any) => typeof r === 'string' ? r : r.key || ''),
                    tenant_name: payload.tenantId || '',
                    access_token: data.access_token,
                    tenants: [],
                    customClaims: payload.customClaims || {},
                  });
                } catch (e) {
                  console.warn('[AuthService] OIDC JWT decode failed:', e);
                }
              }
            }
          } catch (e) {
            console.warn('[AuthService] OIDC code exchange failed:', e);
          }

          // Clean oidc_code from URL
          oidcParams.delete('oidc_code');
          const clean = oidcParams.toString();
          window.history.replaceState(null, '', window.location.pathname + (clean ? '?' + clean : ''));
        } else {
          // Recover JWT from the cookie-backed session endpoint.
          // The HttpOnly cookie survives Safari tab discards and app
          // backgrounding — no sessionStorage dependency.
          let recoveredToken: string | null = null;
          try {
            const sessionXhr = new XMLHttpRequest();
            sessionXhr.open('GET', `/api/v1/oidc/session`, false);
            sessionXhr.withCredentials = true;
            sessionXhr.send();
            if (sessionXhr.status === 200) {
              const data = JSON.parse(sessionXhr.responseText);
              if (data.access_token) {
                recoveredToken = data.access_token;
              }
            }
          } catch (e) {
            console.warn('[AuthService] OIDC session recovery failed:', e);
          }

          if (recoveredToken) {
            // Verify token isn't expired
            try {
              const payload = JSON.parse(atob(recoveredToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
              if (payload.exp && payload.exp * 1000 < Date.now()) {
                recoveredToken = null;
              }
            } catch (e) {
              recoveredToken = null;
            }
          }

          if (recoveredToken) {
            ContextHolder.setAccessToken(recoveredToken);
            this.handoffToken = recoveredToken;
          } else if (this.oidcLoginUrl) {
            // No valid session — redirect to OIDC login
            window.location.href = this.oidcLoginUrl;
            return;
          }
        }
      }

      // Restore auth state from handoff token on page reload
      if (this.handoffToken && !this.plbAuthState) {
        try {
          const payload = JSON.parse(atob(this.handoffToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
          this.setAuthState({
            sub: payload.sub || '',
            name: payload.name || '',
            email: payload.email || '',
            tenant_id: payload.tenantId || '',
            roles: (payload.roles || []).map((r: any) => typeof r === 'string' ? r : r.key || ''),
            tenant_name: payload.tenantId || '',
            access_token: this.handoffToken,
            tenants: [],
            customClaims: payload.customClaims || {},
          });
        } catch (e) { /* ignore */ }
      }
    } else {
      // Fallback — will likely fail but at least doesn't crash
      console.warn('[AuthService] No env config available, using fallback');
      this.authUrl = `https://auth.${window.location.host}`;
      this.authAppUrl = `https://${window.location.host}`;
    }
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  public setAuthState(authState: PlbAuthState) {
    this.plbAuthState = authState;
    this.updateAnalytics(authState);
    this.updateThena(authState);
    this.checkReadOnlyPartnerAccess(authState);
  }

  private checkReadOnlyPartnerAccess(authState: PlbAuthState) {
    if (this._checkedPartnerAccess) return;

    const roles = (authState.roles || []).map((r: any) =>
      (typeof r === 'string' ? r : r.name || r.key || '').toLowerCase()
    );
    const normalizedRoles = roles.map(r => r.replace(/[^a-z0-9]+/g, '_'));
    if (normalizedRoles.length === 0 || !normalizedRoles.every(r => r === 'readonly_partner')) {
      return;
    }

    this._checkedPartnerAccess = true;

    try {
      const xhr = new XMLHttpRequest();
      const baseUrl = this.apiUrl || `https://${window.location.hostname}`;
      xhr.open('GET', `${baseUrl}/api/v1/user-permissions/${authState.sub}`, false);
      xhr.setRequestHeader('Authorization', `Bearer ${authState.access_token}`);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.setRequestHeader('Plb-Branch', 'PROD');
      xhr.send();

      if (xhr.status === 200) {
        const data = JSON.parse(xhr.responseText);
        const filters = data?.record?.dimension_filters || [];
        this.noDataAccess = filters.length === 0;
      }

      if (this.noDataAccess) {
        const configXhr = new XMLHttpRequest();
        const baseUrl = this.apiUrl || `https://${window.location.hostname}`;
        configXhr.open('GET', `${baseUrl}/api/v1/config`, false);
        configXhr.setRequestHeader('Authorization', `Bearer ${authState.access_token}`);
        configXhr.setRequestHeader('Plb-Branch', 'PROD');
        configXhr.send();
        if (configXhr.status === 200) {
          const config = JSON.parse(configXhr.responseText);
          this.noDataAccessLogoUrl = config?.whitelabeling?.logo_path || null;
        }
      }
    } catch (e) {
      console.warn('[AuthService] ReadOnly_Partner access check failed:', e);
    }
  }

  private updateThena(authState: PlbAuthState) {
    try {
      window.thena?.setEmail({
        email: authState.email,
        hashedEmail: authState.customClaims?.thena_hmac,
        name: authState.name,
      });
    } catch (ex) {
      console.log(ex);
    }
  }

  private updateAnalytics(authState: PlbAuthState) {
    try {
      // Log rocket needs an identify call on each page load or the session shows up anonymous
      window.analytics?.identify(authState.sub, {
        name: authState.name,
        email: authState.email,
      });
    } catch (ex) {
      console.log(ex);
    }
  }

  public getAuthState(): PlbAuthState | null {
    // First try to get from instance state
    if (this.plbAuthState) {
      return this.plbAuthState;
    }

    // Then try sessionStorage
    const sessionState = sessionStorage.getItem('plb_auth_state');
    if (sessionState) {
      try {
        this.plbAuthState = JSON.parse(sessionState);
        return this.plbAuthState;
      } catch (e) {
        console.error('Failed to parse auth state from sessionStorage:', e);
      }
    }

    return null;
  }

  public getAccessToken(): string | null {
    // OIDC token (in-memory)
    if (this.handoffToken) {
      return this.handoffToken;
    }

    // First try to get from instance state
    if (this.plbAuthState?.access_token) {
      return this.plbAuthState.access_token;
    }

    // Then try sessionStorage
    const authState = this.getAuthState();
    if (authState?.access_token) {
      return authState.access_token;
    }

    // Finally, try to get from Frontegg's store directly
    // Frontegg stores auth state in localStorage/sessionStorage
    try {
      // Try to get from Frontegg's auth state
      const fronteggState = (window as any).__FRONTEGG__?.authState;
      if (fronteggState?.user?.accessToken) {
        return fronteggState.user.accessToken;
      }

      // Try localStorage (Frontegg might store it there)
      const fronteggAuth = localStorage.getItem('frontegg_auth');
      if (fronteggAuth) {
        const parsed = JSON.parse(fronteggAuth);
        if (parsed?.user?.accessToken) {
          return parsed.user.accessToken;
        }
      }
    } catch (e) {
      console.debug('Could not get token from Frontegg store:', e);
    }

    return null;
  }
}
